import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Shield, Lock, ChevronRight, CheckCircle2, Sparkles, Clock, Video, FileText, Zap, Gift, Star, ArrowLeft, LogIn, UserPlus, MessageCircle, Calendar, Headphones, BookOpen, Trash2, ShoppingCart, Info, ChevronDown, Users } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { useToast } from '../components/ui/Toast';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useUser } from '../contexts/UserContext';
interface CheckoutPageProps {
  onNavigate: (page: string) => void;
}
interface AddOnService {
  id: string;
  title: string;
  price: number;
}
interface AddOnCategory {
  id: string;
  title: string;
  icon: React.ElementType;
  color: string;
  services: AddOnService[];
}
const ALL_ADDON_CATEGORIES: AddOnCategory[] = [{
  id: 'reports',
  title: 'Отчёты',
  icon: FileText,
  color: 'indigo',
  services: [{
    id: 'addon-natal',
    title: 'Натальная карта Premium',
    price: 3900
  }, {
    id: 'addon-compatibility',
    title: 'Совместимость Premium',
    price: 4900
  }, {
    id: 'addon-solar',
    title: 'Соляр Premium',
    price: 4400
  }, {
    id: 'addon-forecast',
    title: 'Прогноз (Транзиты) Premium',
    price: 3900
  }, {
    id: 'addon-combo',
    title: 'Комбо: Натал + Соляр',
    price: 6900
  }]
}, {
  id: 'consultations',
  title: 'Консультации',
  icon: Headphones,
  color: 'sky',
  services: [],
  isNavigable: true
}, {
  id: 'packages',
  title: 'Пакеты',
  icon: Gift,
  color: 'emerald',
  services: [{
    id: 'addon-pkg20',
    title: 'Пакет 20 отчётов',
    price: 19900
  }, {
    id: 'addon-pkg50',
    title: 'Пакет 50 отчётов',
    price: 44900
  }, {
    id: 'addon-pkg100',
    title: 'Пакет 100 отчётов',
    price: 79900
  }]
}];
// Helper to get all flat services for price calculation
const ALL_FLAT_SERVICES = ALL_ADDON_CATEGORIES.flatMap(c => c.services);
export function CheckoutPage({
  onNavigate
}: CheckoutPageProps) {
  const {
    success
  } = useToast();
  const {
    t
  } = useLanguage();
  const {
    items,
    removeItem,
    mainItem
  } = useCart();
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'sbp'>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const {
    isLoggedIn
  } = useUser();
  // Collapsible state for categories (left)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['reports']));
  // Collapsible state for cart items (right)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  // Initialize expanded items based on cart items
  useEffect(() => {
    if (items.length > 0) {
      setExpandedItems(new Set([items[0].id]));
    }
  }, [items.length]);
  // Use all cart items for pricing
  const basePrice = items.length > 0 ? items.reduce((sum, i) => sum + i.price, 0) : 0;
  const toggleAddOn = (id: string) => {
    setSelectedAddOns(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };
  const toggleCategory = (id: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleCartItem = (id: string) => {
    if (items.length <= 1) return; // Don't collapse if only 1 item
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const addOnsTotal = ALL_FLAT_SERVICES.filter(s => selectedAddOns.includes(s.id)).reduce((sum, s) => sum + s.price, 0);
  const subtotal = basePrice + addOnsTotal;
  const discount = promoApplied ? promoDiscount : 0;
  const total = subtotal - discount;
  const handleApplyPromo = () => {
    if (promoCode.toLowerCase() === 'astro10') {
      setPromoApplied(true);
      setPromoDiscount(Math.round(subtotal * 0.1));
    }
  };
  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) return;
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      success(t('checkout.success'));
      onNavigate('order-confirmation');
    }, 1500);
  };
  const steps = [{
    label: t('checkout.step.product'),
    done: true
  }, {
    label: t('checkout.step.checkout'),
    active: true
  }, {
    label: t('checkout.step.payment'),
    done: false
  }];
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  const getColorClasses = (color: string) => {
    switch (color) {
      case 'indigo':
        return 'text-indigo-600 bg-indigo-100 border-indigo-200';
      case 'sky':
        return 'text-sky-600 bg-sky-100 border-sky-200';
      case 'emerald':
        return 'text-emerald-600 bg-emerald-100 border-emerald-200';
      case 'amber':
        return 'text-amber-600 bg-amber-100 border-amber-200';
      default:
        return 'text-slate-600 bg-slate-100 border-slate-200';
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-32 lg:pb-24" data-id="element-21343">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-6" data-id="element-21344">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21345">
          <Breadcrumbs items={[{
          label: t('checkout.breadcrumb.home'),
          onClick: () => onNavigate('home')
        }, {
          label: t('checkout.breadcrumb.marketplace'),
          onClick: () => onNavigate('marketplace')
        }, {
          label: t('checkout.breadcrumb.title')
        }]} className="mb-6" data-id="element-21346" />

          <div className="flex items-center justify-between mb-6" data-id="element-21347">
            <div className="flex items-center gap-3" data-id="element-21348">
              <button onClick={() => onNavigate('product-detail')} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors" aria-label={t('checkout.back')} data-id="element-21349">
                <ArrowLeft className="w-4 h-4" data-id="element-21350" />
              </button>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-21351">
                {t('checkout.title')}
              </h1>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2" data-id="element-21352">
            {steps.map((step, i) => <Fragment key={step.label} data-id="element-21353">
                <div className="flex items-center gap-2" data-id="element-21354">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step.done ? 'bg-emerald-500 text-white' : step.active ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-21355">
                    {step.done ? <CheckCircle2 className="w-4 h-4" data-id="element-21356" /> : i + 1}
                  </div>
                  <span className={`text-sm font-medium ${step.active ? 'text-slate-900' : step.done ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-21357">
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${step.done ? 'bg-emerald-400' : 'bg-slate-200'}`} data-id="element-21358" />}
              </Fragment>)}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-21359">
        <form onSubmit={handlePayment} className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-21360">
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-6" data-id="element-21361">
            {/* Add-on Services (Collapsible) */}
            <Card padding="lg" data-id="element-21362">
              <div className="flex items-center gap-2 mb-2" data-id="element-21363">
                <Sparkles className="w-5 h-5 text-amber-500" data-id="element-21364" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-21365">
                  {t('checkout.addons.title')}
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-6" data-id="element-21366">
                {t('checkout.addons.subtitle')}
              </p>

              <div className="space-y-3" data-id="element-21367">
                {ALL_ADDON_CATEGORIES.map(category => {
                const Icon = category.icon;
                const isExpanded = expandedCategories.has(category.id);
                const selectedCount = category.services.filter(s => selectedAddOns.includes(s.id)).length;
                const colorClasses = getColorClasses(category.color);
                return <div key={category.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-21368">
                      <button type="button" onClick={() => toggleCategory(category.id)} className="w-full flex items-center justify-between p-3.5 hover:bg-slate-50 transition-colors" data-id="element-21369">
                        <div className="flex items-center gap-3" data-id="element-21370">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses}`} data-id="element-21371">
                            <Icon className="w-4 h-4" data-id="element-21372" />
                          </div>
                          <span className="font-semibold text-slate-900 text-sm" data-id="element-21373">
                            {category.title}
                          </span>
                          {selectedCount > 0 && <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-21374">
                              {selectedCount}
                            </span>}
                        </div>
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-21375" />
                      </button>

                      <AnimatePresence initial={false} data-id="element-21376">
                        {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: 'easeInOut'
                    }} data-id="element-21377">
                            <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-slate-100" data-id="element-21378">
                              {(category as any).isNavigable ? <div className="p-3 text-center" data-id="element-21379">
                                  <p className="text-xs text-slate-500 mb-3" data-id="element-21380">
                                    Цена зависит от выбранного астролога и
                                    длительности
                                  </p>
                                  <button type="button" onClick={() => onNavigate('astrologers')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium" data-id="element-21381">
                                    <Users className="w-4 h-4" data-id="element-21382" />
                                    Выбрать астролога
                                  </button>
                                </div> : category.services.map(service => {
                          const isSelected = selectedAddOns.includes(service.id);
                          return <label key={service.id} className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/80 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`} data-id="element-21383">
                                      <div className="flex items-center gap-3 min-w-0" data-id="element-21384">
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleAddOn(service.id)} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0" data-id="element-21385" />
                                        <span className={`text-sm truncate ${isSelected ? 'font-medium text-indigo-900' : 'text-slate-700'}`} data-id="element-21386">
                                          {service.title}
                                        </span>
                                      </div>
                                      <span className={`text-sm font-medium tabular-nums whitespace-nowrap ml-3 ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-21387">
                                        +{formatPrice(service.price)}
                                      </span>
                                    </label>;
                        })}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
              })}
              </div>
            </Card>

            {/* Auth Gate */}
            {!isLoggedIn && <Card padding="lg" className="border-amber-200 bg-amber-50/30" data-id="element-21388">
                <div className="text-center py-4" data-id="element-21389">
                  <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4" data-id="element-21390">
                    <LogIn className="w-7 h-7 text-amber-600" data-id="element-21391" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-21392">
                    {t('checkout.auth.title')}
                  </h2>
                  <p className="text-sm text-slate-600 mb-6 max-w-sm mx-auto" data-id="element-21393">
                    {t('checkout.auth.desc')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xs mx-auto" data-id="element-21394">
                    <Button variant="primary" onClick={() => onNavigate('auth')} icon={<LogIn className="w-4 h-4" data-id="element-21396" />} className="flex-1" data-id="element-21395">
                      {t('checkout.auth.login')}
                    </Button>
                    <Button variant="outline" onClick={() => onNavigate('auth')} icon={<UserPlus className="w-4 h-4" data-id="element-21398" />} className="flex-1" data-id="element-21397">
                      {t('checkout.auth.register')}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1" data-id="element-21399">
                    <Shield className="w-3 h-3" data-id="element-21400" />
                    {t('checkout.auth.hint')}
                  </p>
                </div>
              </Card>}

            {/* Payment Method */}
            <Card padding="lg" className={!isLoggedIn ? 'opacity-40 pointer-events-none select-none' : ''} data-id="element-21401">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-21402">
                {t('checkout.payment.title')}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-21403">
                <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-indigo-500 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-21404">
                  <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500" data-id="element-21405" />
                  <CreditCard className={`w-6 h-6 ml-3 mr-3 ${paymentMethod === 'card' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-21406" />
                  <span className="font-medium text-slate-900" data-id="element-21407">
                    {t('checkout.payment.card')}
                  </span>
                </label>

                <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'sbp' ? 'border-indigo-500 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-21408">
                  <input type="radio" name="payment" value="sbp" checked={paymentMethod === 'sbp'} onChange={() => setPaymentMethod('sbp')} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500" data-id="element-21409" />
                  <div className={`w-6 h-6 ml-3 mr-3 rounded bg-slate-100 flex items-center justify-center font-bold text-xs ${paymentMethod === 'sbp' ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-21410">
                    СБП
                  </div>
                  <span className="font-medium text-slate-900" data-id="element-21411">
                    {t('checkout.payment.sbp')}
                  </span>
                </label>
              </div>

              {paymentMethod === 'card' && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-4" data-id="element-21412">
                  <Input label={t('checkout.card.number')} placeholder="0000 0000 0000 0000" required data-id="element-21413" />
                  <div className="grid grid-cols-2 gap-4" data-id="element-21414">
                    <Input label={t('checkout.card.expiry')} placeholder="MM/YY" required data-id="element-21415" />
                    <Input label={t('checkout.card.cvc')} placeholder="123" type="password" maxLength={3} required data-id="element-21416" />
                  </div>
                  <Input label={t('checkout.card.name')} placeholder="IVAN IVANOV" required data-id="element-21417" />
                </motion.div>}

              {paymentMethod === 'sbp' && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center py-8" data-id="element-21418">
                  <div className="w-48 h-48 bg-slate-100 rounded-2xl mx-auto mb-4 flex items-center justify-center border-2 border-dashed border-slate-300" data-id="element-21419">
                    <span className="text-slate-400 font-medium" data-id="element-21420">
                      {t('checkout.sbp.qr')}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm" data-id="element-21421">
                    {t('checkout.sbp.hint')}
                  </p>
                </motion.div>}
            </Card>

            {/* Contact Info */}
            <Card padding="lg" className={!isLoggedIn ? 'opacity-40 pointer-events-none select-none' : ''} data-id="element-21422">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-21423">
                {t('checkout.contact.title')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-21424">
                <Input label={t('checkout.contact.name')} placeholder={t('checkout.contact.namePlaceholder')} required defaultValue="Анна" data-id="element-21425" />
                <Input label={t('checkout.contact.email')} type="email" placeholder="mail@example.com" required defaultValue="anna@example.com" data-id="element-21426" />
              </div>
              <Input label={t('checkout.contact.phone')} placeholder={t('checkout.contact.phonePlaceholder')} className="mt-4" data-id="element-21427" />
              <p className="text-xs text-slate-500 mt-4 flex items-center" data-id="element-21428">
                <Lock className="w-3 h-3 mr-1.5" data-id="element-21429" />{' '}
                {t('checkout.contact.privacy')}
              </p>
            </Card>
          </div>

          {/* Right Column: Order Summary */}
          <div className="lg:col-span-5" data-id="element-21430">
            <div className="sticky top-24 space-y-6" data-id="element-21431">
              <Card padding="lg" className="border-indigo-100 shadow-lg" data-id="element-21432">
                <h3 className="text-xl font-bold text-slate-900 mb-6" data-id="element-21433">
                  {t('checkout.order.title')}
                </h3>

                {/* Cart Items (Collapsible) */}
                <div className="space-y-3 mb-4" data-id="element-21434">
                  {items.length > 0 ? items.map((item, idx) => {
                  const isExpanded = items.length === 1 || expandedItems.has(item.id);
                  return <motion.div key={item.id} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    x: -20
                  }} transition={{
                    delay: idx * 0.05
                  }} className={`bg-gradient-to-br from-indigo-50 to-slate-50 rounded-xl border border-indigo-100 relative group overflow-hidden ${items.length > 1 ? 'cursor-pointer hover:border-indigo-200' : ''}`} onClick={() => toggleCartItem(item.id)} data-id="element-21435">
                          {/* Collapsed Header */}
                          <div className="p-3.5 flex items-center justify-between gap-3" data-id="element-21436">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1" data-id="element-21437">
                              <div className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" data-id="element-21438" />
                              <h4 className="font-bold text-slate-900 text-sm truncate" data-id="element-21439">
                                {item.name}
                              </h4>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-21440">
                              <span className="text-sm font-bold text-slate-900 tabular-nums" data-id="element-21441">
                                {formatPrice(item.price)}
                              </span>
                              <button type="button" onClick={e => {
                          e.stopPropagation();
                          removeItem(item.id);
                        }} className="w-6 h-6 rounded-full flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors" aria-label={t('checkout.delete')} data-id="element-21442">
                                <Trash2 className="w-3 h-3" data-id="element-21443" />
                              </button>
                              {items.length > 1 && <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-21444" />}
                            </div>
                          </div>

                          {/* Expanded Content */}
                          <AnimatePresence initial={false} data-id="element-21445">
                            {isExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2,
                        ease: 'easeInOut'
                      }} data-id="element-21446">
                                <div className="px-4 pb-4 pt-1 border-t border-indigo-100/50" data-id="element-21447">
                                  <div className="flex items-center gap-2 mb-2" data-id="element-21448">
                                    {item.badges?.map((badge, i) => <Badge key={i} variant={badge.variant as any} className="text-[10px]" data-id="element-21449">
                                        {badge.label}
                                      </Badge>)}
                                  </div>

                                  <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mb-2" data-id="element-21450">
                                    {item.description}
                                  </p>

                                  {(item.format || item.duration) && <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500" data-id="element-21451">
                                      {item.format && <span className="flex items-center" data-id="element-21452">
                                          <Video className="w-3 h-3 mr-1 text-indigo-400" data-id="element-21453" />
                                          {item.format}
                                        </span>}
                                      {item.duration && <span className="flex items-center" data-id="element-21454">
                                          <Clock className="w-3 h-3 mr-1 text-indigo-400" data-id="element-21455" />
                                          {item.duration}
                                        </span>}
                                    </div>}

                                  {item.astrologer && <div className="flex items-center gap-2 mt-3 pt-2 border-t border-indigo-100/80" data-id="element-21456">
                                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0" data-id="element-21457">
                                        {item.astrologer.initials}
                                      </div>
                                      <div className="text-xs font-medium text-slate-700" data-id="element-21458">
                                        {item.astrologer.name}
                                      </div>
                                      <div className="flex items-center gap-0.5 ml-auto" data-id="element-21459">
                                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-21460" />
                                        <span className="text-[11px] font-medium text-slate-600" data-id="element-21461">
                                          {item.astrologer.rating}
                                        </span>
                                      </div>
                                    </div>}

                                  {!item.astrologer && <div className="flex items-center gap-2 mt-3 pt-2 border-t border-indigo-100/80" data-id="element-21462">
                                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-21463">
                                        <FileText className="w-3.5 h-3.5 text-indigo-700" data-id="element-21464" />
                                      </div>
                                      <div className="text-xs font-medium text-slate-700" data-id="element-21465">
                                        {t('checkout.order.autoGeneration')}
                                      </div>
                                    </div>}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </motion.div>;
                }) : <div className="text-center py-6" data-id="element-21466">
                      <ShoppingCart className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-21467" />
                      <p className="text-sm text-slate-400" data-id="element-21468">
                        {t('checkout.order.empty')}
                      </p>
                    </div>}

                  <button type="button" onClick={() => onNavigate('marketplace')} className="w-full py-2.5 px-4 rounded-xl border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2" data-id="element-21469">
                    <span className="text-lg leading-none" data-id="element-21470">+</span>
                    {t('checkout.order.addMore')}
                  </button>
                </div>

                {/* Selected Add-ons */}
                {selectedAddOns.length > 0 && <div className="mb-4 pb-4 border-b border-slate-100 space-y-3" data-id="element-21471">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21472">
                      {t('checkout.order.addonsLabel')}
                    </div>
                    {ALL_FLAT_SERVICES.filter(s => selectedAddOns.includes(s.id)).map(addon => {
                  return <div key={addon.id} className="flex items-center justify-between" data-id="element-21473">
                          <div className="flex items-center gap-2" data-id="element-21474">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" data-id="element-21475" />
                            <span className="text-sm text-slate-700" data-id="element-21476">
                              {addon.title}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-slate-700 tabular-nums whitespace-nowrap" data-id="element-21477">
                            +{formatPrice(addon.price)}
                          </span>
                        </div>;
                })}
                  </div>}

                {/* Promo Code */}
                <div className="mb-6" data-id="element-21478">
                  <div className="flex items-center gap-2" data-id="element-21479">
                    <div className="flex-1" data-id="element-21480">
                      <Input placeholder={t('checkout.promo.placeholder')} value={promoCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPromoCode(e.target.value)} disabled={promoApplied} data-id="element-21481" />
                    </div>
                    <Button variant="outline" type="button" onClick={handleApplyPromo} disabled={promoApplied || !promoCode.trim()} className="flex-shrink-0 h-[42px]" data-id="element-21482">
                      {promoApplied ? t('checkout.promo.applied') : t('checkout.promo.apply')}
                    </Button>
                  </div>
                  {promoApplied && <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="text-xs text-emerald-600 mt-2 flex items-center" data-id="element-21483">
                      <Gift className="w-3 h-3 mr-1" data-id="element-21484" />{' '}
                      {t('checkout.promo.success')}
                    </motion.p>}
                </div>

                {/* Totals */}
                <div className="space-y-2.5 mb-6" data-id="element-21485">
                  {items.length > 0 && <div className="flex justify-between text-sm text-slate-600" data-id="element-21486">
                      <span data-id="element-21487">
                        {items.length > 1 ? t('checkout.total.services', {
                      count: String(items.length)
                    }) : t('checkout.total.service')}
                      </span>
                      <span className="tabular-nums" data-id="element-21488">
                        {formatPrice(basePrice)}
                      </span>
                    </div>}
                  {addOnsTotal > 0 && <div className="flex justify-between text-sm text-slate-600" data-id="element-21489">
                      <span data-id="element-21490">
                        {t('checkout.total.addons', {
                      count: String(selectedAddOns.length)
                    })}
                      </span>
                      <span className="tabular-nums" data-id="element-21491">
                        +{formatPrice(addOnsTotal)}
                      </span>
                    </div>}
                  {discount > 0 && <div className="flex justify-between text-sm text-emerald-600" data-id="element-21492">
                      <span data-id="element-21493">{t('checkout.total.discount')}</span>
                      <span className="tabular-nums" data-id="element-21494">
                        −{formatPrice(discount)}
                      </span>
                    </div>}
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-21495">
                    <span className="text-lg font-bold text-slate-900" data-id="element-21496">
                      {t('checkout.total.total')}
                    </span>
                    <span className="text-2xl font-bold text-indigo-600 tabular-nums" data-id="element-21497">
                      {formatPrice(total)}
                    </span>
                  </div>
                </div>

                {/* Payment hint */}
                {items.length > 1 && <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200/60 mb-5" data-id="element-21498">
                    <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-21499" />
                    <p className="text-xs text-amber-700 leading-relaxed" data-id="element-21500">
                      {t('checkout.payment.hint')}
                    </p>
                  </div>}

                {/* Agreement */}
                <label className="flex items-start gap-3 mb-6 cursor-pointer group" data-id="element-21501">
                  <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-21502" />
                  <span className="text-xs text-slate-500 leading-relaxed group-hover:text-slate-700 transition-colors" data-id="element-21503">
                    {t('checkout.agree.text')}{' '}
                    <button type="button" className="text-indigo-600 hover:underline" data-id="element-21504">
                      {t('checkout.agree.terms')}
                    </button>{' '}
                    {t('checkout.agree.and')}{' '}
                    <button type="button" className="text-indigo-600 hover:underline" data-id="element-21505">
                      {t('checkout.agree.privacy')}
                    </button>
                  </span>
                </label>

                {/* Pay Button */}
                <Button type="submit" size="lg" fullWidth disabled={isProcessing || !agreedToTerms || !isLoggedIn} icon={isProcessing ? undefined : <Lock className="w-4 h-4" data-id="element-21507" />} className="shadow-md shadow-indigo-500/20" data-id="element-21506">
                  {isProcessing ? t('checkout.pay.processing') : !isLoggedIn ? t('checkout.pay.loginRequired') : t('checkout.pay.button', {
                  price: formatPrice(total)
                })}
                </Button>

                {!isLoggedIn && <p className="text-[10px] text-amber-600 text-center mt-2" data-id="element-21508">
                    {t('checkout.login.required')}
                  </p>}
                {isLoggedIn && !agreedToTerms && <p className="text-[10px] text-amber-600 text-center mt-2" data-id="element-21509">
                    {t('checkout.agree.required')}
                  </p>}

                {/* Trust Badges */}
                <div className="mt-6 pt-4 border-t border-slate-100 space-y-2.5" data-id="element-21510">
                  <div className="flex items-center text-xs text-slate-500" data-id="element-21511">
                    <Shield className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-21512" />
                    {t('checkout.trust.ssl')}
                  </div>
                  <div className="flex items-center text-xs text-slate-500" data-id="element-21513">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-21514" />
                    {t('checkout.trust.refund')}
                  </div>
                  <div className="flex items-center text-xs text-slate-500" data-id="element-21515">
                    <Lock className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-21516" />
                    {t('checkout.trust.noSave')}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between lg:hidden z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-21517">
        <div data-id="element-21518">
          <div className="text-xs text-slate-500" data-id="element-21519">
            {t('checkout.mobile.total')}
          </div>
          <div className="text-xl font-bold text-slate-900" data-id="element-21520">
            {formatPrice(total)}
          </div>
        </div>
        <Button size="md" onClick={() => {
        if (agreedToTerms) {
          setIsProcessing(true);
          setTimeout(() => {
            setIsProcessing(false);
            success(t('checkout.successMobile'));
            onNavigate('order-confirmation');
          }, 1500);
        }
      }} disabled={isProcessing || !agreedToTerms} className="shadow-md shadow-indigo-500/20" data-id="element-21521">
          {isProcessing ? t('checkout.pay.processing') : t('checkout.mobile.pay')}
        </Button>
      </div>
    </div>;
}